import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";

export default function Login() {
  const { t, i18n } = useTranslation();
  const { login, user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forceChange, setForceChange] = useState(false);
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const mustChange = forceChange || Boolean(user && user !== null && user.force_password_change);

  const submitForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { default: api } = await import("../lib/api");
      await api.post("/auth/forgot-password", { email });
      setForgotSent(true);
    } catch (err) { setError(apiError(err)); }
    finally { setLoading(false); }
  };

  React.useEffect(() => {
    if (user && !mustChange) navigate("/");
  }, [user, navigate, mustChange]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u?.force_password_change) { setForceChange(true); return; }
      navigate("/");
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (detail === "ACCOUNT_PENDING_APPROVAL") setError(t("login.pendingApproval"));
      else if (detail === "ACCOUNT_REJECTED") setError(t("login.rejected"));
      else if (detail === "ACCOUNT_SUSPENDED") setError(t("login.suspended"));
      else setError(err?.response?.status === 401 ? t("login.error") : apiError(err) || t("login.error"));
    } finally {
      setLoading(false);
    }
  };

  const doChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPwd !== confirmPwd) {
      setError(i18n.language === "fr" ? "Les mots de passe ne correspondent pas." : "Passwords do not match.");
      return;
    }
    try {
      const { default: api } = await import("../lib/api");
      await api.post("/auth/change-password", { new_password: newPwd });
      await refreshMe();
      setForceChange(false);
      navigate("/");
    } catch (err) { setError(apiError(err)); }
  };

  const toggleLang = () => {
    const next = i18n.language === "fr" ? "en" : "fr";
    i18n.changeLanguage(next);
    localStorage.setItem("vytali_lang", next);
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A1F44] text-white flex-col justify-between p-12">
        <div className="flex items-center gap-3 font-heading font-semibold text-xl">
          <img src="/brand/emblem.svg" alt="Vytali Academy" className="h-14 w-auto" />
          {t("brand")}
        </div>
        <div>
          <h1 className="font-heading text-4xl xl:text-5xl font-semibold leading-tight tracking-tight max-w-lg">
            {t("login.subtitle")}
          </h1>
          <p className="mt-6 text-white/70 max-w-md leading-relaxed">{t("login.tagline")}</p>
          <p className="mt-4 text-[#D4B45A] text-xs font-mono-v tracking-[0.3em]">DEPUIS 2003</p>
        </div>
        <p className="text-xs font-mono-v text-white/50">MVP V1 — data_class=DEMO</p>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden flex items-center gap-2 font-heading font-semibold text-lg mb-8">
            <img src="/brand/emblem.svg" alt="Vytali Academy" className="h-10 w-auto" />
            {t("brand")}
          </div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-semibold">{mustChange ? (i18n.language === "fr" ? "Changer le mot de passe" : "Change password") : forgotMode ? t("login.forgot") : t("login.submit")}</h2>
            <button onClick={toggleLang} data-testid="login-lang-toggle"
              className="px-3 py-1 text-xs font-mono-v font-semibold border border-border rounded-md hover:bg-muted transition-colors duration-200 uppercase">
              {i18n.language === "fr" ? "FR → EN" : "EN → FR"}
            </button>
          </div>
          {mustChange ? (
            <form onSubmit={doChangePassword} className="space-y-4" data-testid="force-change-form">
              <p className="text-sm text-[#0A1F44]">{i18n.language === "fr" ? "Veuillez définir un nouveau mot de passe (min. 8 caractères)." : "Please set a new password (min. 8 characters)."}</p>
              <div>
                <label className="block text-sm font-medium mb-1.5">{i18n.language === "fr" ? "Nouveau mot de passe" : "New password"}</label>
                <input data-testid="new-password-input" type="password" required minLength={8} value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  className="w-full px-3 py-2.5 border border-border rounded-md bg-white text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">{i18n.language === "fr" ? "Confirmer le mot de passe" : "Confirm password"}</label>
                <input data-testid="confirm-password-input" type="password" required minLength={8} value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  className="w-full px-3 py-2.5 border border-border rounded-md bg-white text-sm" />
              </div>
              {error && <p data-testid="force-change-error" className="text-sm text-[#FF3B30]">{error}</p>}
              <button data-testid="change-password-submit" type="submit"
                className="w-full py-2.5 bg-[#0A1F44] text-white font-medium rounded-md hover:bg-[#132B5C] transition-colors duration-200">
                {i18n.language === "fr" ? "Enregistrer le nouveau mot de passe" : "Save new password"}
              </button>
            </form>
          ) : forgotMode ? (
            <form onSubmit={submitForgot} className="space-y-4" data-testid="forgot-form">
              <p className="text-sm text-muted-foreground">{t("login.forgotIntro")}</p>
              <div>
                <label className="block text-sm font-medium mb-1.5">{t("login.email")}</label>
                <input data-testid="forgot-email-input" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 border border-border rounded-md bg-white focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none text-sm" />
              </div>
              {forgotSent ? (
                <p data-testid="forgot-success" className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">{t("login.forgotSent")}</p>
              ) : (
                <button data-testid="forgot-submit" type="submit" disabled={loading}
                  className="w-full py-2.5 bg-[#002BF6] text-white font-medium rounded-md hover:bg-[#0024cc] transition-colors duration-200 disabled:opacity-50">
                  {loading ? "…" : t("login.forgotSubmit")}
                </button>
              )}
              <button type="button" data-testid="forgot-back" onClick={() => { setForgotMode(false); setForgotSent(false); setError(""); }}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
                {t("login.backToLogin")}
              </button>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">{t("login.email")}</label>
              <input data-testid="login-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 border border-border rounded-md bg-white focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">{t("login.password")}</label>
              <input data-testid="login-password-input" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 border border-border rounded-md bg-white focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none text-sm" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-[#FF3B30] font-medium">{error}</p>}
            <button data-testid="login-submit-button" type="submit" disabled={loading}
              className="w-full py-2.5 bg-[#002BF6] text-white font-medium rounded-md hover:bg-[#0024cc] transition-colors duration-200 disabled:opacity-50">
              {loading ? "…" : t("login.submit")}
            </button>
            <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotMode(true); setError(""); }}
              className="w-full text-sm text-[#002BF6] hover:underline transition-colors duration-200">
              {t("login.forgot")}
            </button>
            <Link to="/register" data-testid="login-signup-link"
              className="block text-center text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
              {t("login.signUp")}
            </Link>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
